import { useTaskStore } from '@/stores/taskStore'
import { TaskModel } from '@/models/TaskModel'
import type { Task, TaskStatus } from '@/types/Task'
import { useOrganizationStore } from '@/stores/v2/organization'
import { usePersonStore } from '@/stores/v2/person'
import { useTimelineActivityStore } from '@/stores/timelineActivityStore'

export function useTaskService() {
  const taskStore = useTaskStore()
  const organizationStore = useOrganizationStore()
  const personStore = usePersonStore()

  return {
    async createTask(data: Partial<Task>): Promise<Task> {
      const task = TaskModel.create(data)

      if (!TaskModel.validate(task)) {
        throw new Error('Invalid task data')
      }

      // Add denormalized data if needed
      if (task.organizationId) {
        const org = await organizationStore.get(task.organizationId)
        if (org) {
          task.organizationName = org.name
        }
      }

      if (task.personId) {
        const person = await personStore.get(task.personId)
        if (person) {
          task.personName = `${person.firstName} ${person.lastName}`.trim()
        }
      }

      const savedTask = await taskStore.add(task)

      // Add to timeline
      await addTaskToTimeline(savedTask, 'created')

      return savedTask
    },

    async updateTask(id: string, data: Partial<Task>): Promise<Task> {
      const currentTask = taskStore.items.find((t: Task) => t.id === id)
      if (!currentTask) {
        throw new Error('Task not found')
      }

      const updatedTask = TaskModel.create({
        ...currentTask,
        ...data,
        id, // Preserve the original ID
      })

      if (!TaskModel.validate(updatedTask)) {
        throw new Error('Invalid task data')
      }

      // Always update denormalized data if organizationId is present
      if (updatedTask.organizationId) {
        const org = await organizationStore.get(updatedTask.organizationId)
        if (org) {
          updatedTask.organizationName = org.name
        }
      }

      if (updatedTask.personId && updatedTask.personId !== currentTask.personId) {
        const person = await personStore.get(updatedTask.personId)
        if (person) {
          updatedTask.personName = `${person.firstName} ${person.lastName}`.trim()
        }
      }

      await taskStore.update(id, updatedTask)

      // Get the updated task from the store
      const savedTask = taskStore.items.find((t: Task) => t.id === id)
      if (!savedTask) {
        throw new Error('Failed to update task')
      }

      // Add to timeline
      await addTaskToTimeline(savedTask, 'updated')

      return savedTask
    },

    async updateTaskStatus(id: string, status: TaskStatus): Promise<Task> {
      const task = await taskStore.get(id)
      if (!task) throw new Error(`Task with ID ${id} not found`)

      const updatedTask = await this.updateTask(id, {
        status,
        updatedAt: new Date(),
      })

      // Add to timeline
      await addTaskToTimeline({ ...updatedTask, status }, 'updated')

      return updatedTask
    },

    async deleteTask(id: string): Promise<void> {
      await taskStore.remove(id)
    },

    async getTasksByEntity(params: {
      organizationId?: string
      personId?: string
      vehicleId?: string
      transactionId?: string
    }): Promise<Task[]> {
      await taskStore.load()

      return taskStore.items.filter((task: Task) => {
        if (params.organizationId && task.organizationId === params.organizationId) return true
        if (params.personId && task.personId === params.personId) return true
        if (params.vehicleId && task.vehicleId === params.vehicleId) return true
        if (params.transactionId && task.transactionId === params.transactionId) return true
        return false
      })
    },

    async getOverdueTasks(): Promise<Task[]> {
      await taskStore.load()
      return taskStore.items.filter((task: Task) => TaskModel.isOverdue(task))
    },

    async getTasksByStatus(status: TaskStatus): Promise<Task[]> {
      await taskStore.load()
      return taskStore.items.filter((task: Task) => task.status === status)
    },

    async getTasksByAssignee(assignedTo: string): Promise<Task[]> {
      await taskStore.load()
      return taskStore.items.filter((task: Task) => task.assignedTo === assignedTo)
    },

    async completeTask(id: string): Promise<void> {
      const task = await taskStore.get(id)
      if (!task) throw new Error(`Task with ID ${id} not found`)

      await taskStore.update(id, {
        status: 'done',
        completedAt: new Date(),
        updatedAt: new Date(),
      })

      // Add to timeline
      await addTaskToTimeline({ ...task, status: 'done' }, 'completed')
    },

    async getTasksForPerson(personId: string): Promise<Task[]> {
      await taskStore.load()
      return taskStore.findBy((task: Task) => task.personId === personId)
    },

    async getTasksForOrganization(organizationId: string): Promise<Task[]> {
      await taskStore.load()
      return taskStore.findBy((task: Task) => task.organizationId === organizationId)
    },
  }
}

/**
 * Add a task action to the timeline activity store
 */
async function addTaskToTimeline(
  task: Task,
  action: 'created' | 'updated' | 'completed',
): Promise<void> {
  const timelineStore = useTimelineActivityStore()

  let title = 'Task '
  switch (action) {
    case 'created':
      title += 'created'
      break
    case 'updated':
      title += 'updated'
      break
    case 'completed':
      title += 'completed'
      break
  }

  await timelineStore.add({
    type: 'task',
    title,
    description: task.title,
    createdAt: new Date(),
    personId: task.personId,
    organizationId: task.organizationId,
    entityId: task.id!,
    entityType: 'tasks',
    userId: task.assignedTo,
    tags: [task.status],
  })
}
